import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import zodHelper from "@/lib/zod-helper";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useUserRole } from "@/lib/useUserRole";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  InquiryFormData,
  InquiryDayItem,
  InquiryHotelItem,
  TourPackageItem,
} from "@/types/database";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon, Save } from "lucide-react";
import { InquiryDayForm } from "./inquiry-day-form";
import { InquiryHotelForm } from "./inquiry-hotel-form";
import { InquiryTotalSummary } from "./inquiry-total-summary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Define a schema for form validation
const formSchema = zodHelper.object({
  guest_name: zodHelper.string().min(1, "Guest name is required"),
  start_date: zodHelper.string().min(1, "Start date is required"),
  adult_count: zodHelper.number().min(1, "At least 1 adult is required"),
  child_count: zodHelper.number().default(0),
  nights: zodHelper.number().min(1, "Number of nights is required"),
  status: zodHelper.enum(["draft", "new", "sent", "confirmed"]).default("draft"),
  hotel_type: zodHelper.enum(["Direct Booking", "Include Hotel", "No Hotel"]).default("Direct Booking"),
  car_type: zodHelper.string().default("MPV"),
  remarks: zodHelper.string().default(""),
  flight_information: zodHelper.string().default(""),
  total_usd: zodHelper.number().optional(),
});

type InquiryEditFormProps = {
  initialData: InquiryFormData;
  onSubmit: (data: InquiryFormData) => void;
};

export function InquiryEditForm({ initialData, onSubmit }: InquiryEditFormProps) {
  // Get user role to determine if status editing is allowed
  const { role } = useUserRole();
  const canEditStatus = role === 'admin' || role === 'agent';

  // Initialize form with the schema
  const form = useForm<InquiryFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      guest_name: initialData?.guest_name || "",
      start_date: initialData?.start_date || new Date().toISOString(),
      adult_count: initialData?.adult_count || 1,
      child_count: initialData?.child_count || 0,
      nights: initialData?.nights || 1,
      status: initialData?.status || "draft",
      hotel_type: initialData?.hotel_type || "Direct Booking",
      car_type: initialData?.car_type || "MPV",
      remarks: initialData?.remarks || "",
      flight_information: initialData?.flight_information || "",
      total_usd: initialData?.total_usd,
    },
  });

  // State for days and hotels
  const [days, setDays] = useState<InquiryDayItem[]>(initialData?.days || []);
  const [hotels, setHotels] = useState<InquiryHotelItem[]>(initialData?.hotels || []);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formValues, setFormValues] = useState<any>(null);
  const [totalUSD, setTotalUSD] = useState<number | undefined>(initialData?.total_usd);

  const { toast } = useToast();

  const formatIDR = (value: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Fetch settings for USD rate
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("settings")
        .select("*")
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Handle form submission
  const handleSubmit = (values: any) => {
    // Store form values for confirmation
    setFormValues({
      ...values,
      days,
      hotels,
    });

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  // Handle confirmation
  const handleConfirm = () => {
    if (!formValues) return;

    // Create tour_package_json for submission
    const tour_package_json = formValues.days.reduce((acc: any, day: any) => {
      const dayKey = `day_${day.day_number}`;

      // Extract entrance fees and parking from captured_tours
      let entranceAdult = {};
      let entranceChild = {};
      let parking = {};
      let tours = [];

      if (day.captured_tours && day.captured_tours[dayKey]) {
        entranceAdult = day.captured_tours[dayKey].entrance_adult || {};
        entranceChild = day.captured_tours[dayKey].entrance_child || {};
        parking = day.captured_tours[dayKey].parking || {};
        tours = day.captured_tours[dayKey].tours || [];
      }

      acc[dayKey] = {
        tours: tours,
        entrance_adult: entranceAdult,
        entrance_child: entranceChild,
        parking: parking,
        transport_area: day.transport_area,
        transport_cost: day.transport_cost,
        meal_cost: day.meal_cost,
        other_cost: day.other_cost,
        tour_package_name: day.tour_package_name,
        tour_package_id: day.tour_package_id,
        captured_tours: day.captured_tours,
        itinerary: day.captured_tours?.[dayKey]?.itinerary || "",
        day_date: day.day_date || day.captured_tours?.[dayKey]?.day_date || "",
        title: day.title || `Day ${day.day_number} (${day.day_date || day.captured_tours?.[dayKey]?.day_date || ''}) - ${day.tour_package_name || ''}`,
        note: day.note || ""
      };
      return acc;
    }, {});

    // Create hotel_json for submission
    const hotel_json = {
      hotels: formValues.hotels.reduce((acc: any, hotel: any, index: number) => {
        acc[`hotel_${index + 1}`] = {
          hotel_name: hotel.hotel_name,
          checkin_date: hotel.checkin_date,
          checkout_date: hotel.checkout_date,
          nights: hotel.nights,
          room_rate: hotel.room_rate,
          room_count: hotel.room_count,
          total_price: hotel.total_price,
        };
        return acc;
      }, {}),
    };

    // Calculate totals
    const totalTransport = formValues.days.reduce((sum: number, day: InquiryDayItem) => {
      return sum + (day.transport_cost || 0);
    }, 0);

    const totalParking = formValues.days.reduce((sum: number, day: InquiryDayItem) => {
      if (!day.captured_tours) return sum;

      const dayKey = `day_${day.day_number}`;
      if (!day.captured_tours[dayKey] || !day.captured_tours[dayKey].parking) return sum;

      const parkingTotal = Object.values(day.captured_tours[dayKey].parking).reduce((s: number, cost: any) => {
        const numericCost = typeof cost === "number" ? cost : Number(cost) || 0;
        return s + numericCost;
      }, 0);
      return sum + parkingTotal;
    }, 0);

    // Calculate entrance fees using all tour keys
    const totalAdultEntrance = formValues.days.reduce((sum: number, day: InquiryDayItem) => {
      if (!day.captured_tours) return sum;

      const dayKey = `day_${day.day_number}`;
      if (!day.captured_tours[dayKey] || !day.captured_tours[dayKey].entrance_adult) return sum;

      // Get all tour keys from entrance_adult object
      const tourKeys = Object.keys(day.captured_tours[dayKey].entrance_adult);

      return sum + tourKeys.reduce((daySum: number, tour: string) => {
        const entranceFee = day.captured_tours[dayKey].entrance_adult[tour];
        const numericFee = typeof entranceFee === "number" ? entranceFee : Number(entranceFee) || 0;
        const adultCount = typeof formValues.adult_count === "number" ? formValues.adult_count : Number(formValues.adult_count) || 0;
        return daySum + (numericFee * adultCount);
      }, 0);
    }, 0);

    const totalChildEntrance = formValues.days.reduce((sum: number, day: InquiryDayItem) => {
      if (!day.captured_tours) return sum;

      const dayKey = `day_${day.day_number}`;
      if (!day.captured_tours[dayKey] || !day.captured_tours[dayKey].entrance_child) return sum;

      // Get all tour keys from entrance_child object
      const tourKeys = Object.keys(day.captured_tours[dayKey].entrance_child);

      return sum + tourKeys.reduce((daySum: number, tour: string) => {
        const entranceFee = day.captured_tours[dayKey].entrance_child[tour];
        const numericFee = typeof entranceFee === "number" ? entranceFee : Number(entranceFee) || 0;
        const childCount = typeof formValues.child_count === "number" ? formValues.child_count : Number(formValues.child_count) || 0;
        return daySum + (numericFee * childCount);
      }, 0);
    }, 0);

    const totalMealCost = formValues.days.reduce((sum: number, day: InquiryDayItem) => {
      const mealCost = typeof day.meal_cost === "number" ? day.meal_cost : Number(day.meal_cost) || 0;
      return sum + mealCost;
    }, 0);

    const totalOtherCost = formValues.days.reduce((sum: number, day: InquiryDayItem) => {
      const otherCost = typeof day.other_cost === "number" ? day.other_cost : Number(day.other_cost) || 0;
      return sum + otherCost;
    }, 0);

    const totalHotelPrice = formValues.hotels.reduce((sum: number, hotel: InquiryHotelItem) => {
      const totalPrice = typeof hotel.total_price === "number" ? hotel.total_price : Number(hotel.total_price) || 0;
      return sum + totalPrice;
    }, 0);

    const totalEntrance = totalAdultEntrance + totalChildEntrance;
    const grandTotal = totalTransport + totalParking + totalEntrance + totalMealCost + totalOtherCost + totalHotelPrice;

    // Submit the final data
    onSubmit({
      ...formValues,
      tour_package_json,
      hotel_json,
      total_transport_cost: totalTransport,
      total_parking_cost: totalParking,
      total_adult_entrance_fee: totalAdultEntrance,
      total_child_entrance_fee: totalChildEntrance,
      total_entrance_fee: totalEntrance,
      total_meal_cost: totalMealCost,
      total_other_cost: totalOtherCost,
      total_hotel_price: totalHotelPrice,
      grand_total: grandTotal,
      total_usd: formValues.total_usd,
      days,
      hotels,
    });

    // Close the dialog
    setShowConfirmDialog(false);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Guest Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-6 gap-4">
              <FormField
                control={form.control}
                name="guest_name"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Guest name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem className="col-span-1">
                    <FormLabel>Start Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(new Date(field.value), "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={(date) => field.onChange(date.toISOString())}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="adult_count"
                render={({ field }) => (
                  <FormItem className="col-span-1">
                    <FormLabel>Adults</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="child_count"
                render={({ field }) => (
                  <FormItem className="col-span-1">
                    <FormLabel>Children</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nights"
                render={({ field }) => (
                  <FormItem className="col-span-1">
                    <FormLabel>Nights</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem className="col-span-1">
                    <FormLabel>Status</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        if (!canEditStatus) return;
                        field.onChange(value);
                      }}
                      defaultValue={field.value}
                      disabled={!canEditStatus}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="draft" className="text-xs">Draft</SelectItem>
                        <SelectItem value="new" className="text-xs">New</SelectItem>
                        <SelectItem value="sent" className="text-xs">Sent</SelectItem>
                        <SelectItem value="confirmed" className="text-xs">Confirmed</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hotel_type"
                render={({ field }) => (
                  <FormItem className="col-span-1">
                    <FormLabel>Hotel Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select hotel type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Direct Booking" className="text-xs">Direct Booking</SelectItem>
                        <SelectItem value="Include Hotel" className="text-xs">Include Hotel</SelectItem>
                        <SelectItem value="No Hotel" className="text-xs">No Hotel</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="car_type"
                render={({ field }) => (
                  <FormItem className="col-span-1">
                    <FormLabel>Car Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select car type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="MPV" className="text-xs">MPV</SelectItem>
                        <SelectItem value="ELF Short" className="text-xs">ELF Short</SelectItem>
                        <SelectItem value="Hiace" className="text-xs">Hiace</SelectItem>
                        <SelectItem value="ELF Giga" className="text-xs">ELF Giga</SelectItem>
                        <SelectItem value="Bus" className="text-xs">Bus</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="remarks"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Remarks</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Additional notes..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="total_usd"
                render={({ field }) => (
                  <FormItem className="col-span-1">
                    <FormLabel>Total USD</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Enter USD total..."
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="flight_information"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Flight Information</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Flight information..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Day by Day Tour Package */}
        <Card>
          <CardHeader>
            <CardTitle>Day by Day Tour Package</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {days.map((day, index) => (
                <div key={day.day_number} className="bg-white rounded-lg border shadow-sm p-4">
                  <InquiryDayForm
                    day={{ ...day, car_type: form.watch("car_type") }}
                    adult={form.watch("adult_count")}
                    child={form.watch("child_count")}
                    onUpdate={(updatedDay) => {
                      const newDays = [...days];
                      newDays[index] = updatedDay;
                      setDays(newDays);
                    }}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Hotel Information */}
        <Card>
          <CardHeader>
            <CardTitle>Hotel Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <InquiryHotelForm
                hotels={hotels}
                onChange={(newHotels) => setHotels(newHotels)}
                nights={form.watch("nights")}
              />
            </div>
          </CardContent>
        </Card>

        {/* Grand Total Summary */}
        <InquiryTotalSummary
          days={days}
          hotels={hotels}
          adult_count={form.watch("adult_count")}
          child_count={form.watch("child_count")}
          totalUSD={totalUSD}
          onChangeTotalUSD={(newTotalUSD) => setTotalUSD(newTotalUSD)}
        />

        <div className="flex justify-end">
          <Button type="submit" className="w-full md:w-auto">
            <Save className="mr-2 h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </form>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to save these changes? This will update the inquiry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
}
